/**
 * PlanetDragIntegrationService - Connects planet drag to Jataka recalculation
 *
 * Listens to planet.drag.confirmed event and triggers chart recalculation
 * with the selected time from the drag interaction.
 */

import { devLog, devWarn } from '../utils/devLog';
import { globalEventStream, createEvent } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import { jatakaApiService } from './JatakaApiService';
import { jatakaResponseToChartData } from '../utils/apiDataTransformers';
import type { BaseEvent, EventSubscription } from '../components/common/AstrologyChart/universal_architecture/types/EventTypes';
import type { ParsedChartData } from '../models/types';
import type { JatakaRequest } from '../models/api';
import type { SnapshotSource } from '../types/MultipleCharts';

const DEBUG = true; // Enable debug logging

// =============================================================================
// Types
// =============================================================================

interface ChartLocation {
  latitude: number;
  longitude: number;
  timezoneOffset: number;
  placeName?: string;
}

interface RecalculationResult {
  success: boolean;
  chartData?: ParsedChartData;
  error?: string;
}

type ChartDataProvider = (chartId: string) => {
  location: ChartLocation | null;
  chartName: string;
} | null;

type ChartDataUpdater = (chartId: string, chartData: ParsedChartData, source: SnapshotSource) => void;

// =============================================================================
// PlanetDragIntegrationService Class
// =============================================================================

class PlanetDragIntegrationService {
  private subscriptions: EventSubscription[] = [];
  private chartDataProvider: ChartDataProvider | null = null;
  private chartDataUpdater: ChartDataUpdater | null = null;
  private isProcessing = false;

  // ===========================================================================
  // Configuration
  // ===========================================================================

  /**
   * Configure the service with chart data provider and updater
   */
  configure(
    provider: ChartDataProvider,
    updater: ChartDataUpdater
  ) {
    this.chartDataProvider = provider;
    this.chartDataUpdater = updater;
  }

  // ===========================================================================
  // Lifecycle
  // ===========================================================================

  /**
   * Start listening for confirmed events (planet drag and time motion)
   */
  start() {
    if (this.subscriptions.length > 0) return;

    if (DEBUG) devLog('[PlanetDrag:Integration] 🚀 Service started');

    // Listen to both planet drag and time motion confirmed events (DRY)
    this.subscriptions.push(
      globalEventStream.subscribe(
        'planet.drag.confirmed',
        this.handleConfirmed.bind(this)
      )
    );
    this.subscriptions.push(
      globalEventStream.subscribe(
        'time.motion.confirmed',
        this.handleConfirmed.bind(this)
      )
    );
  }

  /**
   * Stop listening for events
   */
  stop() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  // ===========================================================================
  // Event Handler
  // ===========================================================================

  private async handleConfirmed(event: BaseEvent) {
    // Prevent concurrent processing
    if (this.isProcessing) {
      if (DEBUG) devLog('[PlanetDrag:Integration] ⏳ Already processing, skipping');
      return;
    }

    const chartId = event.metadata?.chartId as string;
    const selectedTimeStr = event.metadata?.selectedTime as string;
    const source = event.type; // 'planet.drag.confirmed' or 'time.motion.confirmed'

    if (DEBUG) devLog(`[PlanetDrag:Integration] 📥 Received ${source} for chart ${chartId}, time: ${selectedTimeStr}`);

    if (!chartId || !selectedTimeStr) {
      if (DEBUG) devLog('[PlanetDrag:Integration] ❌ Missing chartId or selectedTime');
      return;
    }
    if (!this.chartDataProvider || !this.chartDataUpdater) {
      devWarn('[PlanetDrag:Integration] ❌ Not configured');
      return;
    }

    // Get chart data
    const chartInfo = this.chartDataProvider(chartId);
    if (!chartInfo || !chartInfo.location) {
      devWarn('[PlanetDrag:Integration] ❌ Chart location not available');
      return;
    }

    if (DEBUG) devLog(`[PlanetDrag:Integration] 📍 Location: ${chartInfo.location.latitude}, ${chartInfo.location.longitude}`);

    this.isProcessing = true;

    try {
      // Emit recalculating event
      globalEventStream.emit(createEvent<BaseEvent>(
        'chart.recalculating',
        'PlanetDragIntegrationService',
        { metadata: { chartId } }
      ));

      // Recalculate with new time
      const result = await this.recalculateChart(
        new Date(selectedTimeStr),
        chartInfo.location,
        chartInfo.chartName
      );

      if (result.success && result.chartData) {
        if (DEBUG) devLog(`[PlanetDrag:Integration] ✅ Recalculation successful, updating chart`);
        // Determine source from event type
        const snapshotSource = source === 'time.motion.confirmed' ? 'time-move' : 'degree-move';
        // Update chart data
        this.chartDataUpdater(chartId, result.chartData, snapshotSource);

        // Emit success event
        globalEventStream.emit(createEvent<BaseEvent>(
          'chart.recalculated',
          'PlanetDragIntegrationService',
          { metadata: { chartId, selectedTime: selectedTimeStr } }
        ));
      } else {
        if (DEBUG) devLog(`[PlanetDrag:Integration] ❌ Recalculation failed: ${result.error}`);
        // Emit error event
        globalEventStream.emit(createEvent<BaseEvent>(
          'chart.recalculation.failed',
          'PlanetDragIntegrationService',
          { metadata: { chartId, error: result.error } }
        ));
      }
    } finally {
      this.isProcessing = false;
    }
  }

  // ===========================================================================
  // Recalculation
  // ===========================================================================

  private async recalculateChart(
    selectedTime: Date,
    location: ChartLocation,
    chartName: string
  ): Promise<RecalculationResult> {
    try {
      // Convert UTC time to local time for API (API expects local time + timezone_offset)
      // Preserve full precision (backend handles microseconds)
      const offsetMs = location.timezoneOffset * 60 * 60 * 1000;
      const localTime = new Date(selectedTime.getTime() + offsetMs);

      // Format as ISO string without timezone suffix (local time), keep milliseconds
      const localDatetime = localTime.toISOString().replace('Z', '');

      devLog(`[PlanetDrag:Integration] 🕐 Time conversion: UTC=${selectedTime.toISOString()}, local=${localDatetime}, offset=${location.timezoneOffset}`);

      // Create JatakaRequest with local time
      const jatakaRequest: JatakaRequest = {
        datetime: localDatetime,
        latitude: location.latitude,
        longitude: location.longitude,
        timezone_offset: Math.round(location.timezoneOffset),
        place_name: location.placeName || 'Unknown',
        altitude: 0,
      };

      // Make API request
      const apiResult = await jatakaApiService.calculateJataka(jatakaRequest);

      if (!apiResult.success || !apiResult.data) {
        return {
          success: false,
          error: apiResult.error?.message || 'API request failed',
        };
      }

      // Create minimal birthData for transformer
      const birthDataForTransform = {
        chartName,
        locationName: location.placeName || 'Unknown',
        date: selectedTime.toISOString().split('T')[0].split('-').reverse().join('-'),
        time: selectedTime.toTimeString().split(' ')[0],
        latitude: location.latitude,
        longitude: location.longitude,
        timezoneMode: 'manual' as const,
        timezoneOffset: this.formatTimezoneOffset(location.timezoneOffset),
        selectedLocation: location.placeName || '',
      };

      // Transform response to chart data
      const chartData = jatakaResponseToChartData(apiResult.data, birthDataForTransform);

      return {
        success: true,
        chartData,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  private formatTimezoneOffset(hours: number): string {
    const sign = hours >= 0 ? '+' : '-';
    const absHours = Math.abs(hours);
    const h = Math.floor(absHours);
    const m = Math.round((absHours - h) * 60);
    return `${sign}${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }
}

// =============================================================================
// Singleton Export
// =============================================================================

export const planetDragIntegrationService = new PlanetDragIntegrationService();
